import httpService from './httpService'

type PublicIdentity = { type?: string; appUserId?: string }

/**
 * The RevenueCat app_user_id this client should configure with, from the generalized public
 * customer-data API (`GET /identities`): the RevenueCat variant's `appUserId` — the
 * `customer_identity` primary, a random UUID once migrated, else the legacy obfuscated DUPR id.
 * Falls back to [fallback] (the obfuscated user id) whenever the endpoint is unavailable; the old id
 * still reverse-resolves to the same DUPR user server-side, so the fallback is safe.
 */
export const resolveRcAppUserId = async (fallback: number | string): Promise<string> => {
  const fb = fallback.toString()
  try {
    const res = (await httpService.Get<{ results?: PublicIdentity[] }>('/identities')) as {
      results?: PublicIdentity[]
    }
    const appUserId = res?.results?.find((d) => d?.type === 'revenuecat')?.appUserId
    return typeof appUserId === 'string' && appUserId.length > 0 ? appUserId : fb
  } catch {
    return fb
  }
}

import { activeSubscriptions } from './auth'
import { DUPR_PLUS_RC_ENTITLEMENT } from '@/hooks/useHasFeature'
import type { SubscriptionResponse } from '@/types'

/**
 * Backend-authoritative entitlement markers from POST /subscription/active (userId-keyed, merged
 * across all of a user's RC customer ids, so it is correct throughout the app_user_id migration).
 * The backend returns the DUPR entitlement vocabulary; we mirror the one gated marker web checks:
 * PREMIUM_L1 (any group) -> the DUPR+ RC entitlement id. Unioned with the RC SDK in App.tsx so a
 * just-purchased user is covered before the RC webhook lands. Returns [] on any failure.
 */
export const fetchBackendEntitlementMarkers = async (): Promise<string[]> => {
  try {
    const res = (await httpService.Post(activeSubscriptions, undefined)) as SubscriptionResponse
    const hasPremiumL1 = (res?.subscriptions ?? []).some((sub) =>
      Object.values(sub?.entitlements ?? {}).some((list) => (list ?? []).includes('PREMIUM_L1'))
    )
    return hasPremiumL1 ? [DUPR_PLUS_RC_ENTITLEMENT] : []
  } catch {
    return []
  }
}
